import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PAMPRestAPIService } from 'src/app/core/api/pamp-rest-api.service';
import { StaffService } from 'src/app/core/api/staff.service';
import {
  pampListFromResponse,
  PampQuestionnaireRecurrence
} from 'src/app/core/models/pamp-todo.model';
import { QuestionnaireRecurrenceItem } from 'src/app/core/models/questionnaire-recurrence.model';
import { questionnaireTypeToFriendlyNameMap } from 'src/app/core/schemas/subscription-plan.schema';
import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';

// The tab intentionally lists the 3-month schedule only; the header copy describes it.
const LISTED_REPEAT_DURATION = '3 months';

const RECURRENCE_I18N = 'clinicSetup.questionnaire.recurrence';

// PAMP stores questionnaireType with arbitrary casing (e.g. 'lagophthalmos', 'BlinkTest'),
// so the friendly-name lookup is keyed by lower-cased type.
const FRIENDLY_NAME_BY_LOWER_CASE_TYPE: { [lowerCaseType: string]: string } = Object.keys(
  questionnaireTypeToFriendlyNameMap
).reduce(
  (lookup, type) => ({
    ...lookup,
    [type.toLowerCase()]: (questionnaireTypeToFriendlyNameMap as { [type: string]: string })[type]
  }),
  {}
);

@Component({
  selector: 'csi-questionnaire-recurrence',
  templateUrl: './questionnaire-recurrence.component.html',
  styleUrls: ['./questionnaire-recurrence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionnaireRecurrenceComponent implements OnInit, OnDestroy {
  public items: QuestionnaireRecurrenceItem[] = [];
  public countLabelKey = `${RECURRENCE_I18N}.questionnaires`;
  public isLoading = true;

  // Which questionnaires recur is edited in PAMP; the same roles that manage clinic settings.
  public readonly canEdit$: Observable<boolean> = this.staffPermissionsService.permissions$.pipe(
    map(permissions => permissions.canManageClinicSettings)
  );

  constructor(
    private pampRestApiService: PAMPRestAPIService,
    private staffService: StaffService,
    private staffPermissionsService: StaffPermissionsService,
    private pampIframeService: PampIframeService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadRecurrences();

    // The PAMP overlay closes when the edit dialog is dismissed (saved or not); refetch so the
    // list reflects whatever was saved.
    this.pampIframeService.iframeClosed$.pipe(untilDestroyed(this)).subscribe(() => {
      this.loadRecurrences();
    });
  }

  ngOnDestroy(): void {}

  public openEditor(): void {
    this.pampIframeService.openQuestionnaireRecurrenceEditor();
  }

  public trackByItemId(_index: number, item: QuestionnaireRecurrenceItem): string {
    return item.id;
  }

  private loadRecurrences(): void {
    this.isLoading = true;
    this.changeDetector.markForCheck();

    this.pampRestApiService
      .getQuestionnaireRecurrences(this.staffService.clinicOwnerId)
      .pipe(
        map(response => this.toRecurrenceItems(pampListFromResponse(response))),
        catchError(error => {
          console.error('Error fetching questionnaire recurrences from PAMP API:', error);
          return of([] as QuestionnaireRecurrenceItem[]);
        }),
        untilDestroyed(this)
      )
      .subscribe(items => {
        this.items = items;
        this.countLabelKey =
          items.length === 1
            ? `${RECURRENCE_I18N}.questionnaire`
            : `${RECURRENCE_I18N}.questionnaires`;
        this.isLoading = false;
        this.changeDetector.markForCheck();
      });
  }

  private toRecurrenceItems(
    recurrences: PampQuestionnaireRecurrence[]
  ): QuestionnaireRecurrenceItem[] {
    return recurrences
      .filter(recurrence => this.isListedRecurrence(recurrence))
      .map((recurrence, index) => ({
        id: recurrence.id || `rec-${index}`,
        name: this.toFriendlyName(recurrence)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private isListedRecurrence(recurrence: PampQuestionnaireRecurrence): boolean {
    const duration = recurrence.repeatInfo && recurrence.repeatInfo.duration;
    return !!duration && duration.trim().toLowerCase() === LISTED_REPEAT_DURATION;
  }

  private toFriendlyName(recurrence: PampQuestionnaireRecurrence): string {
    const content = (recurrence.todoInfo && recurrence.todoInfo.content) || {};
    const questionnaireType = content.questionnaireType || '';
    if (!questionnaireType) {
      return '';
    }
    return (
      FRIENDLY_NAME_BY_LOWER_CASE_TYPE[questionnaireType.toLowerCase()] ||
      content.title ||
      questionnaireType
    );
  }
}
